/**
 * SidewalkGraph — grafo NAVEGÁVEL das CALÇADAS, para os pedestres.
 *
 * Cada quadra "viva" tem um anel de calçada (4 cantos); as arestas do anel
 * fazem o pedestre contornar a quadra SEM pisar na rua. Quadras vizinhas só se
 * conectam por arestas de TRAVESSIA (faixa de pedestre), que cruzam a rua junto
 * às esquinas — assim o pedestre só atravessa a rua na faixa.
 *
 * SUPERQUADRA (Mexican Town): as quadras de um distrito viram UMA. Elas NÃO
 * ganham um anel cada — o anel de uma quadra do miolo correria por cima do canteiro,
 * porque a "rua" que o justificava virou parque. No lugar disso o distrito inteiro
 * recebe a malha do piso (cityLayout.DISTRICT_WALK): anel do perímetro + uma passarela
 * por vão entre canteiros. O pedestre circula pelas quadras livremente, e sempre sobre
 * o piso — nunca sobre o canteiro, nunca por dentro de um prédio.
 *
 * Mesma interface do RoadGraph (nearestNode/randomStep/route) para o CityAgents
 * tratar carros e pessoas com o mesmo navegador, cada um no seu grafo.
 */
import { ROADS } from '../config.js';
import {
  BLOCKS, SIDEWALK, ENTRY_POINTS, DISTRICT_WALK, districtOfBlock, districtPavement
} from './cityLayout.js';

const HALF = SIDEWALK / 2;          // recuo do canto p/ o centro da calçada
const GAP = ROADS.width;            // vão entre quadras vizinhas (= rua)
// Passo máximo entre nós de um trecho reto da superquadra. Um distrito tem 82u de
// lado: só com os cantos, o nó mais próximo de quem sai do carro no meio do
// quarteirão ficaria a 20u — e a "rota" começaria andando para trás. Subdividir
// mantém o nó mais próximo SEMPRE no mesmo trecho de piso em que a pessoa está.
const DISTRICT_STEP = 12;

export class SidewalkGraph {
  constructor() {
    this.nodes = [];
    // conjunto de nós internos da praça (para confinar o passeio) + pontos-chave
    this.plazaNodeSet = new Set();
    this.plazaHubIds = [];   // anel ao redor da fonte (destino de chegada)
    this.plazaTipIds = [];   // pontas dos braços (saída pela calçada mais próxima)
    this.plazaCenter = null;
    // id do distrito → nós da malha dele (superquadra). Confina o passeio de quem chega
    // no `park` daquele distrito, como o plazaNodeSet faz na praça. Vazio sem distrito.
    this.districtNodes = new Map();
    const blocks = [];
    for (const b of BLOCKS) {
      // quadra de superquadra: nada por quadra — o distrito inteiro vira uma malha só
      // (abaixo), e é ELA que entra na lista de travessias, com o terreno dos 82×82
      if (districtOfBlock(b)) continue;
      const L = b.land.left + HALF, R = b.land.right - HALF;
      const T = b.land.top + HALF, B = b.land.bottom - HALF;
      const c = {
        TL: this._add(L, T), TR: this._add(R, T),
        BL: this._add(L, B), BR: this._add(R, B)
      };
      if (b.kind === 'plaza') {
        // Praça: além do anel da calçada, tem os CAMINHOS INTERNOS (cruz + largo da
        // fonte) — os avatares passeiam por eles em vez de estacionar. Ver _buildPlaza.
        this._buildPlaza(b, c, L, R, T, B);
      } else {
        // anel da calçada
        this._link(c.TL, c.TR); this._link(c.TR, c.BR);
        this._link(c.BR, c.BL); this._link(c.BL, c.TL);
      }
      blocks.push({ land: b.land, c });
    }
    for (const W of DISTRICT_WALK) {
      const antes = this.nodes.length;
      blocks.push({ land: W.d.land, c: this._buildDistrict(W) });
      // Nós DESTE distrito (tudo que `_buildDistrict` acabou de criar: anel + passarelas).
      // É o conjunto que confina o passeio de quem chega no parque — mesmo papel do
      // `plazaNodeSet` na praça das outras cidades. Ver CityAgents._stepStroll.
      const ids = [];
      for (let i = antes; i < this.nodes.length; i++) ids.push(i);
      this.districtNodes.set(W.d.id, new Set(ids));
    }

    // travessias (faixas) entre quadras separadas por exatamente uma rua
    for (let i = 0; i < blocks.length; i++) {
      for (let j = 0; j < blocks.length; j++) {
        if (i === j) continue;
        const A = blocks[i], B = blocks[j];
        // B à direita de A (cruza rua vertical)
        if (near(B.land.left - A.land.right, GAP) && overlap(A.land.top, A.land.bottom, B.land.top, B.land.bottom)) {
          this._cross([A.c.TR, A.c.BR], [B.c.TL, B.c.BL], 'z');
        }
        // B abaixo de A (cruza rua horizontal)
        if (near(B.land.top - A.land.bottom, GAP) && overlap(A.land.left, A.land.right, B.land.left, B.land.right)) {
          this._cross([A.c.BL, A.c.BR], [B.c.TL, B.c.TR], 'x');
        }
      }
    }

    // entradas de pedestres: surgem no ponto MAIS DISTANTE da via (borda do mundo,
    // recuado ~8u) e caminham toda a via até a calçada — entrada clara por qual via.
    this.entries = ENTRY_POINTS.map((ep) => {
      const nodeId = this.nearestNode(ep.x, ep.z);
      const n = this.nodes[nodeId];
      const vertical = ep.side === 'top' || ep.side === 'bottom';
      const out = ep.side === 'left' || ep.side === 'top' ? -1 : 1;
      const far = vertical
        ? { x: n.x, z: ep.z - out * 8 }
        : { x: ep.x - out * 8, z: n.z };
      return {
        side: ep.side,
        nodeId,
        spawn: { x: far.x, z: far.z, heading: ep.heading },
        off: { x: far.x, z: far.z }
      };
    });
  }

  /**
   * Monta a malha da SUPERQUADRA a partir da planta do piso (cityLayout.DISTRICT_WALK):
   *
   *   anel do perímetro ─┬─ passarela ─┼─ passarela ─┬─ anel do perímetro
   *                      │             │             │
   *                      └─ passarela ─┴─ passarela ─┘
   *
   * É uma GRADE: uma passarela por vão entre canteiros vizinhos, nos dois eixos, mais o
   * anel do perímetro. Cada linha de centro vira uma corrente de nós; os pontos onde
   * duas linhas se cruzam saem com a MESMA coordenada e o registro por posição os une
   * num nó só — é isso que costura anel e passarelas numa rede conexa, sem precisar
   * procurar interseção nenhuma. Devolve os 4 cantos do anel (as travessias para os
   * vizinhos saem deles, como em qualquer quadra).
   */
  _buildDistrict(W) {
    const reg = new Map(); // "x|z" → id (une os cruzamentos entre linhas)
    const at = (x, z) => {
      const k = `${Math.round(x * 100)}|${Math.round(z * 100)}`;
      let id = reg.get(k);
      if (id == null) reg.set(k, (id = this._add(x, z)));
      return id;
    };
    // trecho reto de `a` a `b`, cortado nos pontos `cuts` (cruzamentos) e subdividido
    // até DISTRICT_STEP. `make` põe a coordenada no eixo certo.
    //
    // RECORTA NO PISO: um lote MESCLADO (o shopping da D2, 2 linhas) atravessa o eixo e
    // o canteiro dele ocupa o miolo inteiro naquele sentido — ou seja, ali a passarela
    // simplesmente NÃO existe. Testar cada ponto e cada meio de trecho contra o piso é o
    // que impede o grafo de inventar um caminho por cima do gramado.
    const run = (a, b, cuts, make) => {
      const lo = Math.min(a, b), hi = Math.max(a, b);
      const stops = new Set([lo, hi]);
      for (const v of cuts) if (v > lo + 0.5 && v < hi - 0.5) stops.add(v);
      const sorted = [...stops].sort((p, q) => p - q);
      const line = [];
      for (let i = 0; i < sorted.length; i++) {
        if (i > 0) {
          const gap = sorted[i] - sorted[i - 1];
          const n = Math.ceil(gap / DISTRICT_STEP);
          for (let k = 1; k < n; k++) line.push(sorted[i - 1] + (gap * k) / n);
        }
        line.push(sorted[i]);
      }
      let prev = null; // ponto anterior JÁ no piso (o nó só nasce quando há aresta)
      for (const v of line) {
        const p = make(v);
        if (!districtPavement(p.x, p.z)) { prev = null; continue; }
        if (prev && districtPavement((prev.x + p.x) / 2, (prev.z + p.z) / 2)) {
          if (prev.id == null) prev.id = at(prev.x, prev.z);
          p.id = at(p.x, p.z);
          this._link(prev.id, p.id);
        }
        prev = p;
      }
    };
    const hline = (z, x0, x1, cuts) => run(x0, x1, cuts, (x) => ({ x, z }));
    const vline = (x, z0, z1, cuts) => run(z0, z1, cuts, (z) => ({ x, z }));

    const { x0, x1, z0, z1, xLines, zLines } = W;
    // anel do perímetro (calçada de 5u), cortado nas passarelas
    hline(z0, x0, x1, xLines);
    hline(z1, x0, x1, xLines);
    vline(x0, z0, z1, zLines);
    vline(x1, z0, z1, zLines);
    // passarelas: uma por vão entre canteiros vizinhos, de ponta a ponta do distrito.
    // O trecho tapado por um lote MESCLADO (o shopping engole um vão inteiro) é recortado
    // pelo teste de piso dentro de `run` — a linha existe, o pedaço sob a grama não.
    for (const xl of xLines) vline(xl, z0, z1, zLines);
    for (const zl of zLines) hline(zl, x0, x1, xLines);

    return { TL: at(x0, z0), TR: at(x1, z0), BL: at(x0, z1), BR: at(x1, z1) };
  }

  /**
   * Monta os caminhos NAVEGÁVEIS internos da praça (quadra da rotatória), casando
   * com o desenho do chão (CityTexture.drawPlaza): 4 BRAÇOS em cruz das calçadas
   * até o largo central, e um ANEL octogonal ao redor da fonte (largo pavimentado).
   * Os avatares entram por um braço, dão a volta na fonte pelo anel e saem por outro.
   *
   *   ponta do braço (na calçada) ─ braço ─ nó cardeal do anel ─ … anel … ─ cardeal ─ braço ─ ponta
   *
   * Também mantém o anel da calçada (via as 4 pontas), para que quem chega de uma
   * quadra vizinha (travessia → canto) alcance os braços.
   */
  _buildPlaza(b, c, L, R, T, B) {
    const cx = (b.land.left + b.land.right) / 2;
    const cz = (b.land.top + b.land.bottom) / 2;
    // pontas dos braços = meio de cada lado, na linha da calçada
    const topTip = this._add(cx, T);
    const botTip = this._add(cx, B);
    const leftTip = this._add(L, cz);
    const rightTip = this._add(R, cz);
    // anel da calçada passando PELAS pontas (divide cada lado ao meio)
    this._link(c.TL, topTip); this._link(topTip, c.TR);
    this._link(c.TR, rightTip); this._link(rightTip, c.BR);
    this._link(c.BR, botTip); this._link(botTip, c.BL);
    this._link(c.BL, leftTip); this._link(leftTip, c.TL);
    // anel octogonal ao redor da fonte (raio entre a bacia da fonte ~4.3 e a borda
    // do largo pavimentado ~7; por dentro dos bancos, que ficam a ~6.2 nas diagonais)
    const RH = 5.5;
    const hub = [];
    for (let k = 0; k < 8; k++) {
      const th = (k * Math.PI) / 4; // 0=N, 2=L, 4=S, 6=O
      hub.push(this._add(cx + RH * Math.sin(th), cz - RH * Math.cos(th)));
    }
    for (let k = 0; k < 8; k++) this._link(hub[k], hub[(k + 1) % 8]);
    // braços: ponta (calçada) → nó cardeal do anel
    this._link(topTip, hub[0]);
    this._link(rightTip, hub[2]);
    this._link(botTip, hub[4]);
    this._link(leftTip, hub[6]);
    // registra o conjunto interno (canto + pontas + anel) e os pontos-chave
    this.plazaTipIds = [topTip, rightTip, botTip, leftTip];
    this.plazaHubIds = hub.slice();
    this.plazaCenter = { x: cx, z: cz };
    for (const id of [c.TL, c.TR, c.BL, c.BR, ...this.plazaTipIds, ...hub]) this.plazaNodeSet.add(id);
  }

  /** Nó do anel da fonte mais próximo de (x,z) — destino ao CHEGAR na praça. */
  plazaEntryNode(x, z) {
    return this._nearestIn(this.plazaHubIds, x, z);
  }

  /** Nó da malha do distrito `did` mais próximo de (x,z) — por onde o passeio começa. */
  districtEntryNode(did, x, z) {
    const set = this.districtNodes.get(did);
    if (!set || !set.size) return this.nearestNode(x, z);
    return this._nearestIn([...set], x, z);
  }

  /** Passo aleatório CONFINADO ao distrito `did`: nunca pega uma travessia e sai da
   *  superquadra. Espelha o plazaWanderStep; sem o distrito, cai no passo normal. */
  districtWanderStep(did, nodeId, prevId, rnd) {
    const set = this.districtNodes.get(did);
    if (!set || !set.size) return this.randomStep(nodeId, prevId, rnd);
    const nbr = this.nodes[nodeId].nbr.filter((n) => set.has(n));
    if (!nbr.length) return this.randomStep(nodeId, prevId, rnd); // nó fora do distrito
    const opts = nbr.filter((n) => n !== prevId);
    const pool = opts.length ? opts : nbr;
    return pool[Math.floor(rnd() * pool.length)];
  }

  /** Ponta de braço mais próxima de (x,z) — por onde SAIR da praça rumo à calçada. */
  plazaExitTipNode(x, z) {
    return this._nearestIn(this.plazaTipIds, x, z);
  }

  /** Passo aleatório CONFINADO à praça: só anda entre os nós internos (não pega uma
   *  travessia e sai da praça). Evita voltar pelo nó anterior quando dá. */
  plazaWanderStep(nodeId, prevId, rnd) {
    const set = this.plazaNodeSet;
    const nbr = this.nodes[nodeId].nbr.filter((n) => set.has(n));
    if (!nbr.length) return this.randomStep(nodeId, prevId, rnd); // fallback (nó fora da praça)
    const opts = nbr.filter((n) => n !== prevId);
    const pool = opts.length ? opts : nbr;
    return pool[Math.floor(rnd() * pool.length)];
  }

  _nearestIn(ids, x, z) {
    let best = ids.length ? ids[0] : this.nearestNode(x, z), bd = Infinity;
    for (const id of ids) {
      const n = this.nodes[id];
      const d = (n.x - x) ** 2 + (n.z - z) ** 2;
      if (d < bd) { bd = d; best = id; }
    }
    return best;
  }

  _add(x, z) {
    const id = this.nodes.length;
    this.nodes.push({ id, x, z, nbr: [] });
    return id;
  }

  _link(a, b) {
    if (!this.nodes[a].nbr.includes(b)) this.nodes[a].nbr.push(b);
    if (!this.nodes[b].nbr.includes(a)) this.nodes[b].nbr.push(a);
  }

  /** Liga cada canto de A ao canto de B mais alinhado (faixa de travessia). */
  _cross(aIds, bIds, axis) {
    for (const a of aIds) {
      const na = this.nodes[a];
      let best = -1, bd = Infinity;
      for (const b of bIds) {
        const d = Math.abs((axis === 'z' ? this.nodes[b].z - na.z : this.nodes[b].x - na.x));
        if (d < bd) { bd = d; best = b; }
      }
      if (best !== -1 && bd < 14) this._link(a, best);
    }
  }

  node(id) {
    return this.nodes[id];
  }

  nearestNode(x, z) {
    let best = -1, bd = Infinity;
    for (const n of this.nodes) {
      const d = (n.x - x) ** 2 + (n.z - z) ** 2;
      if (d < bd) { bd = d; best = n.id; }
    }
    return best;
  }

  randomStep(nodeId, prevId, rnd) {
    const nbr = this.nodes[nodeId].nbr;
    const opts = nbr.filter((n) => n !== prevId);
    const pool = opts.length ? opts : nbr;
    return pool[Math.floor(rnd() * pool.length)];
  }

  route(start, goal) {
    if (start === goal) return [goal];
    const N = this.nodes.length;
    const dist = new Array(N).fill(Infinity);
    const prev = new Array(N).fill(-1);
    const done = new Array(N).fill(false);
    dist[start] = 0;
    for (let it = 0; it < N; it++) {
      let u = -1, bd = Infinity;
      for (let i = 0; i < N; i++) if (!done[i] && dist[i] < bd) { bd = dist[i]; u = i; }
      if (u === -1 || u === goal) break;
      done[u] = true;
      const nu = this.nodes[u];
      for (const v of nu.nbr) {
        const w = Math.hypot(this.nodes[v].x - nu.x, this.nodes[v].z - nu.z);
        if (dist[u] + w < dist[v]) { dist[v] = dist[u] + w; prev[v] = u; }
      }
    }
    const path = [];
    for (let at = goal; at !== -1; at = prev[at]) path.unshift(at);
    return path[0] === start ? path : [start, goal];
  }
}

function near(v, target) {
  return Math.abs(v - target) < 3;
}
function overlap(a0, a1, b0, b1) {
  return Math.min(a1, b1) - Math.max(a0, b0) > 4;
}
